
import React, { useState } from 'react';
import { generateWhatsAppLink } from '../services/subscription';
import { WHATSAPP_NUMBER, GOLD_DAYS } from '../constants';
import { UserProfile } from '../types';
import { getFreePlanStatus, getGoldPlanStatus } from '../services/subscriptionStatus';

interface PaywallProps {
  onUpgrade: (code: string) => void;
  onBack: () => void;
  profile: UserProfile;
  t: any;
  lang: 'fr' | 'en';
}

const PaywallView: React.FC<PaywallProps> = ({ onUpgrade, onBack, profile, t, lang }) => {
  const [code, setCode] = useState('');
  const [showInput, setShowInput] = useState(false);

  const freeStatus = getFreePlanStatus(profile);
  const goldStatus = getGoldPlanStatus(profile);
  const silverRemaining = freeStatus.remainingDays;
  const isSilverActive = freeStatus.isActive;
  const isGoldActive = goldStatus.isActive;

  return (
    <div className="flex-1 p-6 flex flex-col bg-gray-50 overflow-y-auto pb-10 relative">
      {/* Back Button */}
      <div className="absolute top-4 left-4 z-20">
        <button 
          onClick={onBack}
          className="w-10 h-10 bg-white shadow-sm border border-gray-100 rounded-full flex items-center justify-center text-gray-500 active:scale-95 transition-all"
          aria-label={t.back}
        >
          <i className="fa-solid fa-arrow-left"></i>
        </button>
      </div>

      <div className="flex flex-col items-center mt-8 mb-8">
        <div className="w-16 h-16 gold-gradient rounded-full flex items-center justify-center shadow-lg mb-4">
          <i className="fa-solid fa-crown text-white text-3xl"></i>
        </div>
        <h2 className="text-2xl font-black text-gray-900">{t.choosePlan}</h2>
        <p className="text-gray-500 text-center text-sm px-4 mt-2">
          {t.goldFeatures}
        </p>
      </div>

      <div className="space-y-4">
        {/* Plan Silver Card */}
        <div className={`p-5 rounded-3xl border-2 transition-all bg-white ${isSilverActive ? 'border-gray-200' : 'border-red-100 opacity-75'}`}>
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="text-xl font-bold text-gray-800">{t.planSilver}</h3>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{t.freeTrial}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${isSilverActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {isSilverActive ? t.activeStatus : t.expiredStatus}
            </span>
          </div>
          <div className="mt-4 flex items-baseline gap-1">
             <span className="text-3xl font-black text-gray-900">{silverRemaining}</span>
             <span className="text-gray-500 text-sm font-medium">{t.remainingDays}</span>
          </div>
        </div>

        {/* Plan Gold Card */}
        <div className={`p-5 rounded-3xl border-2 transition-all shadow-sm relative overflow-hidden bg-white border-yellow-400`}>
          <div className="absolute -right-6 -top-6 w-20 h-20 gold-gradient opacity-10 rounded-full"></div>
          
          <div className="flex justify-between items-start mb-2 relative z-10">
            <div>
              <h3 className="text-xl font-bold text-yellow-700">{t.planGold}</h3>
              <p className="text-xs text-yellow-600/60 font-medium uppercase tracking-wider">{t.goldPremium}</p>
            </div>
            {isGoldActive ? (
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">
                {t.activeStatus}
              </span>
            ) : (
              <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">
                10$ • {GOLD_DAYS} {t.days}
              </span>
            )}
          </div>

          <ul className="mt-4 space-y-2 relative z-10">
            <li className="flex items-center gap-2 text-sm text-gray-600">
              <i className="fa-solid fa-circle-check text-yellow-500 text-xs"></i>
              Accès illimité aux 3 livres
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-600">
              <i className="fa-solid fa-circle-check text-yellow-500 text-xs"></i>
              Analyses IA approfondies
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-600">
              <i className="fa-solid fa-circle-check text-yellow-500 text-xs"></i>
              Support prioritaire WhatsApp
            </li>
          </ul>

          {isGoldActive && (
            <div className="mt-4 text-sm font-bold text-yellow-700"> 
              {goldStatus.remainingDays} {t.remainingDays}
            </div>
          )}

          {!isGoldActive && (
            <div className="mt-6 space-y-3">
              <a
                href={generateWhatsAppLink(WHATSAPP_NUMBER, lang)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowInput(true)}
                className="w-full bg-green-500 text-white font-bold py-3 rounded-2xl text-center shadow-md hover:bg-green-600 active:scale-95 transition-all flex items-center justify-center gap-2 text-sm"
              >
                <i className="fa-brands fa-whatsapp text-lg"></i>
                {t.getGoldCode}
              </a>

              {showInput && (
                <div className="mt-4 pt-4 border-t space-y-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">{t.enterGoldCode}</p>
                  <input
                    type="text"
                    maxLength={12}
                    placeholder="0000 0000 0000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full p-3 border-2 border-yellow-100 rounded-xl text-center text-lg tracking-widest font-mono focus:border-yellow-400 outline-none bg-yellow-50/30 text-black"
                  />
                  <button
                    onClick={() => onUpgrade(code)}
                    disabled={code.length !== 12}
                    className="w-full gold-gradient text-white font-bold py-3 rounded-xl shadow-md disabled:opacity-50 text-sm"
                  >
                    {t.validate}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="mt-10 text-[10px] text-gray-400 text-center px-6 leading-relaxed">
        L'activation GOLD dure 30 jours calendaires. <br/>
        Local-first, Zero-knowledge, Confidentialité totale.
      </p>
    </div>
  );
};

export default PaywallView;
